/** @prettier */

/**
 * グループ室内機編集ダイアログ
 */

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';

//MTH component import
import TransferList from '../../component/TransferList';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  getCompositionsGroupsByGroupID,
  patchCompositionsGroups,
  getCompositionsGateway,
  getCompositionsSLUnit,
  getCompositionsSLIFUnit,
} from '../../API/API_Collection';
//TypeDefine import
import {regunitType} from '../../Type/TypeDef';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {iconWhite, limit} from '../../define';

import {useTranslation} from 'react-i18next';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';

const GroupUnitEditDialog = (props: {
  allGroupInfo: apiIF.res.getCompositionsGroups | null;
  displayGroupInfo: apiIF.res.getCompositionsGroupsByGroupID;
  setDisplayGroupInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsGroupsByGroupID | null>
  >;
}) => {
  //パスパラメータ取得
  const {siteID, groupID} = useParams();

  //prettier-ignore
  const [unregisteredIUInit, setUnregisteredIUInit] = useState<regunitType[]>([]); //未登録室内機（初期値）
  const [unregisteredIU, setUnregisteredIU] = useState<regunitType[]>([]); //未登録室内機
  const [registeredIUInit, setRegisteredIUInit] = useState<regunitType[]>([]); //登録室内機（初期値）
  const [registeredIU, setRegisteredIU] = useState<regunitType[]>([]); //登録室内機
  const [slInfo, setSLInfo] = useState<apiIF.res.getCompositionsSLUnit[]>([]);
  //prettier-ignore
  const [slOtherInfo, setSLOtherInfo] = useState<apiIF.res.getCompositionsSLIFUnit[]>([]);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false); //SAVEボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  const trans = useTranslation().t; //翻訳関数

  useEffect(() => {
    //パスパラメータを取得できない場合
    //グループの情報を取得できない場合
    if (
      siteID === undefined ||
      groupID === undefined ||
      props.allGroupInfo === null ||
      props.displayGroupInfo === null
    ) {
      return;
    }

    setRegisteredIUInit(props.displayGroupInfo['unit-informations']);
    setRegisteredIU(props.displayGroupInfo['unit-informations']);

    //未登録室内機の抽出
    //x、y座標情報を付加する
    const unregistrationUnit = props.allGroupInfo['unregistered-sl-unit'].map(
      unitInfo => ({
        ...unitInfo,
        'x-coordinate': '-1',
        'y-coordinate': '-1',
      })
    );
    setUnregisteredIUInit(unregistrationUnit);
    setUnregisteredIU(unregistrationUnit);

    getCompositionsGateway(siteID, {
      'fields': 'system',
      'max-result': 100,
    }).then(response => {
      const gwInfo = response as {
        'gateway-informations': {'gateway-id': string; 'system': string}[];
      };
      //全空調機情報取得
      getSLInfoAll(gwInfo['gateway-informations']);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.allGroupInfo]);

  /**
   * 全空調機情報取得
   * @param gwInfo サイトに登録されているすべてのゲートウェイの情報
   * @returns
   */
  const getSLInfoAll = (
    gwInfo: {
      'gateway-id': string;
      'system': string;
    }[]
  ) => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    let slPromises = [];
    let slOtherPromises = [];
    for (let info of gwInfo) {
      if (info['system'] !== 'sl') continue;
      //SL通信設定の空調機の情報を取得
      slPromises.push(
        getCompositionsSLUnit(siteID, info['gateway-id'], {'max-result': 400})
      );
      //その他の空調機の情報を取得
      slOtherPromises.push(
        getCompositionsSLIFUnit(siteID, info['gateway-id'], {'max-result': 400})
      );
    }

    Promise.all(slPromises).then(response => {
      setSLInfo(response);
    });
    Promise.all(slOtherPromises).then(response => {
      setSLOtherInfo(response);
    });
  };

  /**
   * Unit Editボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenEditDialog(true);
  };

  /**
   * SAVEボタン押下時処理
   */
  const editUnit = () => {
    //パスパラメータを取得できない場合
    //グループの情報を取得できない場合
    if (siteID === undefined || groupID === undefined) {
      return;
    }

    setSaveBtnDisabled(true);
    setAPIWait(true);

    //編集情報の作成
    const editData: apiIF.reqParameters.patchCompositionsGroups = {
      'unit-informations': registeredIU.map(info => ({
        'unit-id': info['unit-id'],
        'x-coordinate': info['x-coordinate'],
        'y-coordinate': info['y-coordinate'],
      })),
    };
    patchCompositionsGroups(siteID, groupID, editData)
      .then(() => {
        setUnregisteredIUInit(unregisteredIU);
        setRegisteredIUInit(registeredIU);
        //グループの情報取得
        return getCompositionsGroupsByGroupID(siteID, groupID);
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['unit-informations'].sort(sortByUnicode('unit-name'));
        props.setDisplayGroupInfo(response);
        setRegisteredIU(response['unit-informations']);
        setIsOpenEditDialog(false);
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setSaveBtnDisabled(false);
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setUnregisteredIU(unregisteredIUInit);
    setRegisteredIU(registeredIUInit);
    setIsOpenEditDialog(false);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleClickOpen}
        id="mv-btnRegUnit"
        startIcon={
          <img
            className="groupSetting__contents__button__icon"
            src={iconWhite.EDIT.src}
            alt={`${trans(
              `GroupUnitEditDialog.image.alternativeText.${iconWhite.EDIT.alt}`
            )}`}
          />
        }>
        {trans('GroupUnitEditDialog.buttonTxt.unitEdit')}
      </Button>
      <Dialog
        open={isOpenEditDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        id="mv-groupIuRegistDialog"
        fullWidth={true}
        maxWidth={'lg'}>
        <DialogTitle id="form-dialog-title">
          {trans('GroupUnitEditDialog.dialogBox.title')}
        </DialogTitle>
        {/* リスト表示（左側に未登録室内機、右側に登録室内機の情報を付与) */}
        <DialogContent>
          <DialogContentText>
            {trans('GroupUnitEditDialog.dialogBox.msg')}
            {registeredIU.length > limit.REGISTRATION_NUM_GROUP_IU_MAX ? (
              <Box className="groupSetting__contents__message--iuOver">
                {trans('GroupUnitEditDialog.dialogBox.errorMsg.registerLimit')}
              </Box>
            ) : (
              <></>
            )}
          </DialogContentText>
          <TransferList
            left={unregisteredIU}
            setLeft={setUnregisteredIU}
            right={registeredIU}
            setRight={setRegisteredIU}
            slInfo={slInfo}
            slOtherInfo={slOtherInfo}
          />
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('GroupUnitEditDialog.dialogBox.buttonTxt.cancel')}
            id={'mv-groupUnitEditCancelBtn'}
          />
          <PrimaryButton
            onClick={editUnit}
            buttonText={trans('GroupUnitEditDialog.dialogBox.buttonTxt.save')}
            id="mv-btnUnitRegSave"
            disabled={
              saveBtnDisabled ||
              registeredIU.length > limit.REGISTRATION_NUM_GROUP_IU_MAX
            }
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={'mv-groupUnitEditErrDlgBtn'}
      />
    </>
  );
};
export default GroupUnitEditDialog;
